import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from "@myela/ui";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import type { Announcement } from "@/lib/api";

interface AnnouncementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcement: Announcement;
  /** Zero-based position within the unseen queue. */
  index: number;
  total: number;
  /** Advance to the next announcement, or finish on the last one. */
  onNext: () => void;
}

export function AnnouncementDialog({
  open,
  onOpenChange,
  announcement,
  index,
  total,
  onNext,
}: AnnouncementDialogProps) {
  const isLast = index >= total - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* z-100: sit above the floating chat panel (z-70) on the dashboard.
          Block outside-click / focus-steal dismissal so the dialog only closes
          via the ✕ or Next/Done buttons — otherwise the chat panel or a toast
          initialising can fire an outside interaction that closes (and marks
          everything seen) the moment it opens, so it intermittently never shows. */}
      <DialogContent
        className="z-100 sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex h-44 items-center justify-center overflow-hidden">
          <img
            src={announcement.imageUrl || "/assets/login-hero.svg"}
            alt=""
            className="h-full w-full object-contain p-4"
          />
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            What&apos;s new
          </p>
          <DialogHeader className="space-y-1">
            <DialogTitle>{announcement.title}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              {formatDate(announcement.publishedAt)}
            </p>
          </DialogHeader>
          <DialogDescription className="whitespace-pre-line text-sm text-muted-foreground">
            {announcement.body}
          </DialogDescription>
        </div>

        <DialogFooter className="flex-row items-center justify-between">
          {total > 1 ? (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === index
                      ? "w-4 bg-primary"
                      : "w-1.5 bg-muted-foreground/30",
                  )}
                />
              ))}
            </div>
          ) : (
            <span />
          )}
          <Button onClick={onNext} className="bg-clip-border">
            {isLast ? "Done" : "Next"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
